import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Switch } from "~/components/ui/switch";
import {
  thoughtLeaderQueryKeys,
  thoughtLeaderQueryOptions,
  updateThoughtLeaderFn,
  type ThoughtLeaderRow,
} from "~/lib/functions/cih/thought-leaders";
import {
  IP_SENSITIVITY_OPTIONS,
  PARTNER_TIER_OPTIONS,
  type IpSensitivity,
  type PartnerTier,
} from "~/lib/shared/content-status";

export const Route = createFileRoute(
  "/admin/cih/thought-leaders/$thoughtLeaderId",
)({
  staticData: { title: "Edit Thought Leader" },
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(
      thoughtLeaderQueryOptions(params.thoughtLeaderId),
    );
    return { title: data?.name ?? "Edit Thought Leader" };
  },
  component: ThoughtLeaderEditorPage,
});

function ThoughtLeaderEditorPage() {
  const { thoughtLeaderId } = Route.useParams();
  const { data: thoughtLeader, isLoading } = useQuery(
    thoughtLeaderQueryOptions(thoughtLeaderId),
  );

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!thoughtLeader) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Thought leader not found.</div>
      </div>
    );
  }

  return (
    <ThoughtLeaderEditorForm
      thoughtLeaderId={thoughtLeaderId}
      thoughtLeader={thoughtLeader}
    />
  );
}

function ThoughtLeaderEditorForm({
  thoughtLeaderId,
  thoughtLeader,
}: {
  thoughtLeaderId: string;
  thoughtLeader: ThoughtLeaderRow;
}) {
  const queryClient = useQueryClient();
  const [successMessage, setSuccessMessage] = useState("");

  const form = useForm({
    defaultValues: {
      name: thoughtLeader.name,
      organization: thoughtLeader.organization,
      website: thoughtLeader.website,
      ipSensitivity: (thoughtLeader.ipSensitivity as IpSensitivity) ?? "medium",
      partnerTier: (thoughtLeader.partnerTier as PartnerTier | null) ?? null,
      cautiousFlag: thoughtLeader.cautiousFlag,
    },
  });

  const showSuccess = (msg: string): void => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      const v = form.state.values;
      return updateThoughtLeaderFn({
        data: {
          id: thoughtLeaderId,
          name: v.name,
          organization: v.organization,
          website: v.website,
          ipSensitivity: v.ipSensitivity,
          partnerTier: v.partnerTier,
          cautiousFlag: v.cautiousFlag,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: thoughtLeaderQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: thoughtLeaderQueryKeys.detail(thoughtLeaderId),
      });
      showSuccess("Thought leader saved.");
    },
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6">
          <h1 className="text-2xl font-medium text-gray-900">
            {thoughtLeader.name}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Thought leader editor — ID: {thoughtLeaderId}
          </p>
        </div>

        {successMessage && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {updateMutation.error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {updateMutation.error instanceof Error
              ? updateMutation.error.message
              : "An error occurred. Please try again."}
          </div>
        )}

        <Card className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <form.Field name="name">
              {(field) => (
                <div>
                  <Label htmlFor="tl-name" className="mb-1">
                    Name
                  </Label>
                  <Input
                    id="tl-name"
                    type="text"
                    value={field.state.value}
                    onValueChange={field.handleChange}
                    onBlur={field.handleBlur}
                  />
                </div>
              )}
            </form.Field>
            <div>
              <Label htmlFor="tl-slug" className="mb-1">
                Slug
              </Label>
              <Input
                id="tl-slug"
                type="text"
                value={thoughtLeader.slug ?? ""}
                readOnly
                className="bg-gray-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <form.Field name="organization">
              {(field) => (
                <div>
                  <Label htmlFor="tl-org" className="mb-1">
                    Organization
                  </Label>
                  <Input
                    id="tl-org"
                    type="text"
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="e.g., Acquisition.com"
                  />
                </div>
              )}
            </form.Field>
            <form.Field name="website">
              {(field) => (
                <div>
                  <Label htmlFor="tl-website" className="mb-1">
                    Website
                  </Label>
                  <Input
                    id="tl-website"
                    type="text"
                    value={field.state.value ?? ""}
                    onChange={(e) => field.handleChange(e.target.value || null)}
                    placeholder="https://..."
                  />
                </div>
              )}
            </form.Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <form.Field name="ipSensitivity">
              {(field) => (
                <div>
                  <Label htmlFor="tl-ip" className="mb-1">
                    IP sensitivity
                  </Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(val) =>
                      field.handleChange(val as IpSensitivity)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {IP_SENSITIVITY_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
            <form.Field name="partnerTier">
              {(field) => (
                <div>
                  <Label htmlFor="tl-tier" className="mb-1">
                    Partner tier{" "}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </Label>
                  <Select
                    value={field.state.value ?? "__none__"}
                    onValueChange={(val) =>
                      field.handleChange(
                        val === "__none__" ? null : (val as PartnerTier),
                      )
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {PARTNER_TIER_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          </div>

          <form.Field name="cautiousFlag">
            {(field) => (
              <div className="flex items-center gap-3">
                <Switch
                  id="tl-cautious"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
                <Label htmlFor="tl-cautious">
                  Cautious flag — attribute conservatively (high-IP author)
                </Label>
              </div>
            )}
          </form.Field>
        </Card>

        <div className="mt-4 flex justify-end">
          <Button
            variant="primary"
            onClick={() => updateMutation.mutate()}
            loading={updateMutation.isPending}
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
